import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { BotDoc, SceneDoc } from '@spirits/shared';
import { functions } from '../firebase';
import { ChatPanel } from '../chat/ChatPanel';
import { SceneSetting } from './SceneSetting';

const setTargetCallable = httpsCallable<
  { sceneId: string; botId: string | null },
  { ok: boolean }
>(functions, 'setTarget');

const HEARTBEAT_MS = 20_000;

interface Props {
  bot: (BotDoc & { id: string }) | null;
  scene?: SceneDoc | null;
  soulCount?: number;
  // True iff a strictly-lower-ordered bot in the same scene is still deciding.
  // While locked, the chat history is shown but whispers + roads are gated.
  locked?: boolean;
  blockedBy?: (BotDoc & { id: string }) | null;
  // The next bot the player can act on (null when none / already selected).
  nextBot?: (BotDoc & { id: string }) | null;
  onSelectNext?: (id: string) => void;
  // False when viewing a bot from an earlier (frozen) scene — read-only.
  isCurrent?: boolean;
  onClose: () => void;
}

export function GuidancePanel({
  bot,
  scene,
  soulCount = 0,
  locked = false,
  blockedBy = null,
  nextBot = null,
  onSelectNext,
  isCurrent = true,
  onClose,
}: Props) {
  // Heartbeat presence while a bot is selected.
  useEffect(() => {
    if (!bot) return;
    const tick = () =>
      setTargetCallable({ sceneId: bot.sceneId, botId: bot.id }).catch(() => {/* ignore */});
    tick();
    const t = setInterval(tick, HEARTBEAT_MS);
    return () => {
      clearInterval(t);
      setTargetCallable({ sceneId: bot.sceneId, botId: null }).catch(() => {/* ignore */});
    };
  }, [bot?.id, bot?.sceneId]);

  if (!bot) {
    return (
      <div className="h-full flex items-center justify-center p-8 border-l border-slate-900">
        <div className="max-w-md w-full space-y-5 text-center">
          <SceneSetting scene={scene} />
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-slate-700/60" />
            <span className="uppercase tracking-widest whitespace-nowrap">
              {soulCount > 0
                ? `${soulCount} here await a voice`
                : 'no one here yet'}
            </span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-700/60" />
          </div>
          {soulCount > 0 && <p className="text-xs text-slate-600">Choose someone to begin guiding them.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 min-w-0 border-l border-slate-900 bg-slate-900/40">
      <PanelHeader bot={bot} onClose={onClose} />
      <ChatPanel
        bot={bot}
        locked={locked}
        blockedBy={blockedBy}
        nextBot={nextBot}
        onSelectNext={onSelectNext}
        isCurrent={isCurrent}
      />
    </div>
  );
}

function PanelHeader({ bot, onClose }: { bot: BotDoc & { id: string }; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const decided = bot.decisionState === 'decided';
  const decision = bot.decision;
  // Subtle "who they're becoming" hint — the arc they're drifting toward, derived
  // server-side from the run's history. Absent in the opening; sharpens ◦→● on lock.
  const arc = bot.currentArcId ? bot.arcs?.find((a) => a.id === bot.currentArcId) : undefined;

  return (
    <div className="flex-none border-b border-slate-800">
      <div className="p-4 pb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-slate-200 -ml-1 px-1 text-xl leading-none"
            title="back"
          >
            ←
          </button>
          <h2 className="text-2xl text-slate-100 truncate">{bot.displayName}</h2>
          <span className={`text-[10px] uppercase tracking-widest ${decided ? 'text-emerald-400/80' : 'text-amber-300/70'}`}>
            {decided ? 'decided ✦' : 'deciding'}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-slate-500 hover:text-slate-300 text-base leading-none"
            title={expanded ? 'collapse' : 'expand'}
            aria-expanded={expanded}
          >
            <span className={`inline-block transition-transform ${expanded ? '' : '-rotate-90'}`}>ℹ️</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="hidden lg:block text-slate-500 hover:text-slate-300 text-lg leading-none"
            title="leave them"
          >
            ✕
          </button>
        </div>
      </div>

      {arc && (
        <p
          className={`px-4 pb-2 -mt-1 text-[11px] leading-snug ${bot.arcLocked ? 'text-violet-300/75' : 'text-violet-300/50'}`}
          title={bot.arcLocked ? 'who they have become' : 'who they are drifting toward'}
        >
          <span className="mr-1.5">{bot.arcLocked ? '●' : '◦'}</span>
          <span className="uppercase tracking-widest text-[10px] text-violet-400/55 mr-1.5">
            {bot.arcLocked ? 'became' : 'becoming'}
          </span>
          {arc.title}
        </p>
      )}

      {expanded && bot.aspiration && (
        <p className="px-4 pt-1 text-sm leading-snug text-violet-300/70">
          <span className="not-italic text-[10px] uppercase tracking-widest text-violet-400/60 mr-2">aspiration</span>
          {bot.aspiration}
        </p>
      )}

      {expanded && !decided && decision && (decision.wish || decision.fear) && (
        <div className="px-4 pb-3 pt-2 space-y-1">
          {decision.wish && (
            <p className="text-sm leading-snug text-amber-200/75">
              <span className="not-italic text-[10px] uppercase tracking-widest text-amber-400/60 mr-2">wish</span>
              {decision.wish}
            </p>
          )}
          {decision.fear && (
            <p className="text-sm leading-snug text-rose-300/70">
              <span className="not-italic text-[10px] uppercase tracking-widest text-rose-400/60 mr-2">fear</span>
              {decision.fear}
            </p>
          )}
        </div>
      )}

      {expanded && decided && bot.committedAction && (
        <p className="px-4 pb-3 pt-1 text-sm text-emerald-300/80 leading-snug">
          <span className="not-italic text-[10px] uppercase tracking-widest text-emerald-400/60 mr-2">chose</span>
          {bot.committedAction.label}
        </p>
      )}
    </div>
  );
}
